import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { analyticsTrack } from '../../analyticsUtils';
import { getRequest } from '../../requestUtils';
import NavBar from './NavBar';
import ProjectDetailsNav from './ProjectDetailsNav';
import ProjectName from './ProjectName';
import ProjectExpertise from './ProjectExpertise';
import ProjectExpertisePriority from './ProjectExpertisePriority';
import ProjectScope from './ProjectScope';
import ProjectResponsibilities from './ProjectResponsibilities';
import ProjectBudgetLogistics from './ProjectBudgetLogistics';
import ProjectReview from './ProjectReview';
import FeaturedProfileBanner from './FeaturedProfileBanner';
import './ProjectDetails.scss';

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

class ProjectDetails extends Component {
  constructor(props) {
    super(props);

    this.state = {
      steps: [
        'start',
        'description',
        'qualifications',
        'expertise-priority',
        'budget & logistics',
        'review',
      ],
      step: props.step,
      validatedStep: props.validatedStep || 'review',
      projectResourceId: props.projectResourceId,
      fromReviewPage: false,
    };

    this.getProjectDetails = this.getProjectDetails.bind(this);
    this.handleUpdateSection = this.handleUpdateSection.bind(this);
    this.handleSetProjectResourceId = this.handleSetProjectResourceId.bind(this);
    this.handleReturnToProjectPage = this.handleReturnToProjectPage.bind(this);
  }

  getProjectDetails(formStep = '') {
    const { projectResourceId } = this.state;

    if (projectResourceId) {
      return getRequest(`/projects/${projectResourceId}?form_step=${formStep}`);
    }
    return {};
  }

  getButtonProps() {
    const { fromReviewPage, step, steps } = this.state;
    const { returnToProjectPage } = this.props;
    const currentStepIndex = steps.indexOf(step);

    if (fromReviewPage) {
      const after = step === 'qualifications'
        ? this.handleUpdateSection('expertise-priority', true) : this.handleUpdateSection('review');
      return {
        afterSubmit: after,
        back: this.handleUpdateSection('review'),
        backButtonText: 'Cancel',
        submitButtonText: 'Save',
      };
    } if (returnToProjectPage) {
      const after = step === 'qualifications' ? this.handleUpdateSection('expertise-priority', false) : this.handleReturnToProjectPage;
      const back = step === 'expertise-priority' ? this.handleUpdateSection('qualifications', false) : this.handleReturnToProjectPage;
      const submitButtonText = step === 'qualifications' ? 'Next' : 'Save';
      const backButtonText = step === 'expertise-priority' ? 'Back' : 'Cancel';
      return {
        afterSubmit: after,
        back,
        backButtonText,
        submitButtonText,
      };
    } if (currentStepIndex === 0 && this.props.duplicationProjectResourceId) {
      return {
        afterSubmit: this.handleUpdateSection('review'),
        back: () => history.back(),
        backButtonText: 'Back',
        submitButtonText: 'Save',
      };
    } if (currentStepIndex === steps.length - 1) {
      return {};
    }
    const goBack = currentStepIndex === 0
      ? () => history.back()
      : this.handleUpdateSection(steps[currentStepIndex - 1]);

    const nextSection = steps[currentStepIndex + 1];
    const goNext = this.handleUpdateSection(nextSection);

    return {
      afterSubmit: goNext,
      back: goBack,
      backButtonText: 'Back',
      submitButtonText: step === 'qualifications' ? 'Next' : `Next: ${capitalize(nextSection)}`,
    };
  }

  handleReturnToProjectPage() {
    const { projectResourceId } = this.props;
    window.location.href = `/projects/${projectResourceId}`;
  }

  handleSetProjectResourceId(callback) {
    return (
      (projectResourceId) => {
        this.setState({ projectResourceId }, callback);
      }
    );
  }

  handleUpdateSection(nextStep, fromReviewPage = false) {
    const { steps } = this.state;
    const { userId, newProjectDetails } = this.props;
    return (
      () => {
        const { validatedStep, step, projectResourceId } = this.state;
        const nextOrBack = steps.indexOf(nextStep) > steps.indexOf(step);
        const newValidatedStep = (steps.indexOf(nextStep) > steps.indexOf(validatedStep))
          ? nextStep : validatedStep;
        analyticsTrack(
          `Clicked next from ${step} section`,
          {
            location: 'Project creation',
            eventId: nextOrBack ? `next-click-${step}` : `back-click-${step}`,
            eventType: 'buttonClick',
            timestamp: Date.now(),
            buttonId: nextOrBack ? `next-button-${step}` : `back-button-${step}`,
            buttonLabel: nextOrBack ? `Next: ${nextStep}` : `Back: ${nextStep}`,
            wizardStep: nextOrBack ? steps.indexOf(step) + 2 : steps.indexOf(step),
            wizardTotalSteps: steps.length,
            userId,
          },
        );
        this.setState({
          validatedStep: newValidatedStep,
          step: nextStep,
          fromReviewPage,
        });
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        if (projectResourceId) {
          window.history.pushState(null, newProjectDetails.name, `/projects/${projectResourceId}/edit?step=${nextStep}`);
        }
      }
    );
  }

  renderNameSection() {
    const {
      projectUrl,
      csrfToken,
      isAdmin,
      companyPicklist,
      companyId,
      projectName,
      duplicationProjectResourceId,
      rehireUserResourceId,
      favoritedConsultant,
      newProjectDetails,
      userId,
      projectTemplatesPath,
      hoursPerWeekPicklist,
      showFullTimeOption,
      projectTemplateId,
      jobDescriptionUpload,
    } = this.props;
    const buttonProps = this.getButtonProps();

    return (
      <ProjectName
        formType="page"
        projectUrl={projectUrl}
        projectName={projectName || ''}
        duplicationProjectResourceId={duplicationProjectResourceId}
        rehireUserResourceId={rehireUserResourceId}
        csrfToken={csrfToken}
        setProjectResourceId={this.handleSetProjectResourceId}
        projectResourceId={this.state.projectResourceId}
        getProjectDetails={this.getProjectDetails}
        isAdmin={isAdmin}
        companyPicklist={companyPicklist || []}
        companyId={companyId}
        favoritedConsultant={favoritedConsultant}
        newProjectDetails={newProjectDetails}
        userId={userId}
        projectTemplatesPath={projectTemplatesPath}
        hoursPerWeekPicklist={hoursPerWeekPicklist}
        showFullTimeOption={showFullTimeOption}
        projectTemplateId={projectTemplateId}
        jobDescriptionUpload={jobDescriptionUpload}
        {...buttonProps}
      />
    );
  }

  renderExpertiseSection() {
    const { csrfToken } = this.props;
    const buttonProps = this.getButtonProps();

    return (
      <ProjectExpertise
        formType="page"
        csrfToken={csrfToken}
        projectResourceId={this.state.projectResourceId}
        getProjectDetails={this.getProjectDetails}
        {...buttonProps}
      />
    );
  }

  renderExpertisePrioritySection() {
    const { csrfToken } = this.props;
    const buttonProps = this.getButtonProps();

    return (
      <ProjectExpertisePriority
        formType="page"
        csrfToken={csrfToken}
        projectResourceId={this.state.projectResourceId}
        getProjectDetails={this.getProjectDetails}
        {...buttonProps}
      />
    );
  }

  renderResponsibilitiesSection() {
    const { csrfToken } = this.props;
    const buttonProps = this.getButtonProps();

    return (
      <ProjectResponsibilities
        formType="page"
        csrfToken={csrfToken}
        projectResourceId={this.state.projectResourceId}
        getProjectDetails={this.getProjectDetails}
        {...buttonProps}
      />
    );
  }

  renderScopeSection() {
    const { csrfToken } = this.props;
    const buttonProps = this.getButtonProps();

    return (
      <ProjectScope
        formType="page"
        csrfToken={csrfToken}
        projectResourceId={this.state.projectResourceId}
        getProjectDetails={this.getProjectDetails}
        {...buttonProps}
      />
    );
  }

  renderBudgetLogisticsSection() {
    const { csrfToken, showBackgroundCheck } = this.props;
    const buttonProps = this.getButtonProps();

    return (
      <ProjectBudgetLogistics
        formType="page"
        csrfToken={csrfToken}
        projectResourceId={this.state.projectResourceId}
        getProjectDetails={this.getProjectDetails}
        showBackgroundCheck={showBackgroundCheck}
        {...buttonProps}
      />
    );
  }

  renderReviewSection() {
    const {
      projectUrl, csrfToken, showAddlRequirements, isAdmin, projectTypes,
      userId, homePath, showFullTimeOption,
    } = this.props;
    const { projectResourceId, step, steps } = this.state;

    return (
      <ProjectReview
        projectUrl={projectUrl}
        csrfToken={csrfToken}
        showAddlRequirements={showAddlRequirements}
        handleUpdateSection={this.handleUpdateSection}
        projectResourceId={projectResourceId}
        getProjectDetails={this.getProjectDetails}
        steps={steps}
        isAdmin={isAdmin}
        projectTypes={projectTypes}
        openAdminModal={step === 'admin'}
        userId={userId}
        homePath={homePath}
        showFullTimeOption={showFullTimeOption}
      />
    );
  }

  renderSection() {
    const { step } = this.state;

    switch (step.replace(/%20/g, ' ')) {
      case 'start':
        return this.renderNameSection();
      case 'qualifications':
        return this.renderExpertiseSection();
      case 'expertise-priority':
        return this.renderExpertisePrioritySection();
      case 'description':
      case 'responsibilities':
        return this.renderResponsibilitiesSection();
      case 'scope':
        return this.renderScopeSection();
      case 'logistics':
      case 'budget ':
      case 'budget & logistics':
        return this.renderBudgetLogisticsSection();
      default:
    }

    return null;
  }

  render() {
    const { step, validatedStep, projectResourceId } = this.state;
    const {
      favoritedConsultantName, reviewProjectPath,
      homePath, projectsPath,
    } = this.props;

    if (step === 'review' || step === 'admin') return (this.renderReviewSection());
    return (
      <Fragment>
        <ProjectDetailsNav
          isProjectNew={reviewProjectPath === null}
          displayExit
          reviewPath={reviewProjectPath}
          homePath={homePath}
          projectsPath={projectsPath}
        />
        <div className="project-details-screen container-md">
          {favoritedConsultantName
            && (
            <FeaturedProfileBanner
              consultantName={favoritedConsultantName}
            />
            )}
          <NavBar
            activeSection={step}
            handleUpdateSection={this.handleUpdateSection}
            validatedStep={validatedStep}
            projectResourceId={projectResourceId}
          />
          <div className="edit-project-container">
            <div className="spacer m-0" />
            { this.renderSection() }
          </div>
        </div>
      </Fragment>
    );
  }
}

ProjectDetails.propTypes = {
  projectResourceId: PropTypes.string,
  projectUrl: PropTypes.string.isRequired,
  projectName: PropTypes.string,
  duplicationProjectResourceId: PropTypes.string,
  rehireUserResourceId: PropTypes.string,
  csrfToken: PropTypes.string.isRequired,
  step: PropTypes.string,
  isAdmin: PropTypes.bool,
  companyPicklist: PropTypes.arrayOf(PropTypes.shape({})),
  companyId: PropTypes.string,
  showAddlRequirements: PropTypes.bool,
  projectTypes: PropTypes.arrayOf(PropTypes.shape({})),
  returnToProjectPage: PropTypes.bool,
  validatedStep: PropTypes.string,
  favoritedConsultant: PropTypes.string,
  favoritedConsultantName: PropTypes.string,
  userId: PropTypes.string.isRequired,
  newProjectDetails: PropTypes.shape({}),
  projectTemplatesPath: PropTypes.string,
  hoursPerWeekPicklist: PropTypes.arrayOf(PropTypes.shape({})),
  reviewProjectPath: PropTypes.string,
  formType: PropTypes.string,
  homePath: PropTypes.string.isRequired,
  projectsPath: PropTypes.string.isRequired,
  showFullTimeOption: PropTypes.bool.isRequired,
  projectTemplateId: PropTypes.number,
  jobDescriptionUpload: PropTypes.bool,
  showBackgroundCheck: PropTypes.bool,
};

ProjectDetails.defaultProps = {
  formType: 'page',
  projectResourceId: null,
  step: 'start',
  validatedStep: 'review',
  duplicationProjectResourceId: null,
  rehireUserResourceId: null,
  companyPicklist: [],
  isAdmin: false,
  companyId: null,
  projectName: null,
  showAddlRequirements: false,
  projectTypes: [],
  returnToProjectPage: false,
  favoritedConsultant: null,
  favoritedConsultantName: null,
  newProjectDetails: {},
  projectTemplatesPath: null,
  hoursPerWeekPicklist: [],
  reviewProjectPath: null,
  projectTemplateId: null,
  jobDescriptionUpload: false,
  showBackgroundCheck: false,
};

export default ProjectDetails;
